import { z } from "zod";

import { CountryCode } from "../models/partials/CountryCode";
import { UsStateCode } from "../models/partials/UsStateCode";
import { TeamBillingInfoData } from "../models/Team";
import { ErrorResponse, SuccessResponse } from "./standard-responses";

export const settingsEndpoints = {
  "up.billing.subscription.cancel": (p: { teamDomain: string }) => [
    "up",
    "delete",
    `/settings/teams/${p.teamDomain}/billing/subscription`,
  ],
  "up.billing.subscription.resume": (p: { teamDomain: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/billing/subscription/on`,
  ],
  "up.billing.update": (p: { teamDomain: string }) => ["up", "put", `/settings/teams/${p.teamDomain}/billing/details`],
  "up.billing.sessionCredit.settings.update": (p: { teamDomain: string }) => [
    "up",
    "put",
    `/settings/teams/${p.teamDomain}/billing/session-credit/settings`,
  ],

  "up.settings.integrations.salesforce.setup": (p: { teamDomain: string; sandbox: boolean }) => [
    "up",
    "get",
    `/settings/teams/${p.teamDomain}/integrations/add/salesforce${p.sandbox ? "?sandbox=true" : ""}`,
  ],
} as const satisfies Record<string, (p: any) => ["up", "get" | "delete" | "post" | "put", `/${string}`]>;

export const settingsRequests = {
  "up.billing.subscription.cancel": z.object({
    teamDomain: z.string(),
    cancelReason: z.string(),
    cancelDetails: z.string().optional(),
  }),
  "up.billing.subscription.resume": z.object({
    teamDomain: z.string(),
  }),
  "up.billing.update": z.object({
    teamDomain: z.string(),
    vatNumber: z.string().optional().nullable(),
    invoiceName: z.string().optional().nullable(),
    invoiceAddress: z.string().optional().nullable(),
    invoiceCountry: CountryCode.nullable(),
    invoiceZip: z.string().optional().nullable(),
    invoiceState: UsStateCode.optional().nullable(),
    invoiceEmail: z.string().optional().nullable(),
  }),
  "up.billing.sessionCredit.settings.update": z.object({
    teamDomain: z.string(),
    team: TeamBillingInfoData.pick({
      settingSessionTimeCreditAutoTopUpEnabled: true,
      settingSessionTimeCreditAutoTopUpTrigger: true,
      settingSessionTimeCreditAutoTopUpMultiplier: true,
    }),
  }),

  "up.settings.integrations.salesforce.setup": z.object({
    teamDomain: z.string(),
    sandbox: z.boolean().optional(),
  }),
} as const;

export const settingsResponses = {
  "up.billing.subscription.cancel": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.subscription.resume": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),
  "up.billing.update": SuccessResponse.extend({
    invoiceDetails: z.object({
      invoiceAddress: z.string().nullable(),
      invoiceCountry: z.string().nullable(),
      invoiceEmail: z.string().nullable(),
      invoiceName: z.string().nullable(),
      invoiceZip: z.string().nullable(),
      invoiceState: z.string().nullable(),
      vatNumber: z.string().nullable(),
    }),
  }).or(
    ErrorResponse.extend({
      error: z.literal("validation_error"),
      teamErrors: z.record(settingsRequests["up.billing.update"].keyof(), z.string()),
    }),
  ),
  "up.billing.sessionCredit.settings.update": SuccessResponse.extend({
    team: TeamBillingInfoData,
  }),

  "up.settings.integrations.salesforce.setup": SuccessResponse,
} as const;
